import React from 'react';
import {
  DollarSign, Send, Loader2, TrendingUp, ExternalLink, Clock,
  Navigation, Plane, Car, CheckCircle, AlertCircle
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';

const startIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36"><path fill="#FF6B00" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"/><circle fill="white" cx="12" cy="12" r="6"/><text x="12" y="16" text-anchor="middle" fill="#FF6B00" font-size="10" font-weight="bold">A</text></svg>`),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
});

const endIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36"><path fill="#000000" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"/><circle fill="white" cx="12" cy="12" r="6"/><text x="12" y="16" text-anchor="middle" fill="#000000" font-size="10" font-weight="bold">B</text></svg>`),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
});

function FitBounds({ from, to }) {
  const map = useMap();
  React.useEffect(() => {
    if (from && to) {
      const bounds = L.latLngBounds([[from.lat, from.lng], [to.lat, to.lng]]);
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [map, from, to]);
  return null;
}

export function OfferModal({
  t, request, suggestedPrice, offerPrice, setOfferPrice,
  offerNotes, setOfferNotes, submitting, routeCoordinates,
  onSubmit, onClose, formatLocation, openInGoogleMaps,
  vehicles = [], selectedVehicleId, setSelectedVehicleId,
}) {
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);
  const [commissionPercent, setCommissionPercent] = React.useState(15);

  React.useEffect(() => {
    // Load current commission live so driver sees correct net
    (async () => {
      try {
        const { api } = await import('../../services/api');
        const res = await api.get('/driver/commission');
        if (typeof res?.data?.percent === 'number') setCommissionPercent(res.data.percent);
      } catch { /* fallback to default 15% */ }
    })();
  }, []);

  const priceNum = parseFloat(offerPrice) || 0;
  const commissionEur = priceNum > 0 ? Math.round(priceNum * commissionPercent) / 100 : 0;
  const netEur = priceNum > 0 ? Math.round((priceNum - commissionEur) * 100) / 100 : 0;

  const approvedVehicles = vehicles.filter(v => v.status === 'approved');
  const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId);

  // Auto-select first approved vehicle if none selected
  React.useEffect(() => {
    if (!selectedVehicleId && approvedVehicles.length > 0) {
      setSelectedVehicleId(approvedVehicles[0].id);
    }
  }, [approvedVehicles.length]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 sm:flex sm:items-center sm:justify-center sm:p-4" onClick={onClose}>
      <div className="bg-white sm:rounded-2xl w-full h-full sm:h-auto sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 sm:p-6 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-slate-800 truncate">{t('driver_make_offer_title')}</h2>
            <p className="hidden sm:block text-sm text-slate-500 mt-1">{t('driver_make_offer_desc')}</p>
          </div>
          <button onClick={onClose} className="sm:hidden text-slate-400 hover:text-slate-600 p-2 -mr-2 shrink-0" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-3 sm:space-y-4 flex-1 overflow-y-auto overflow-x-hidden">
          {/* Vehicle Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <Car size={14} className="inline mr-1" /> Fahrzeug auswählen *
            </label>
            {approvedVehicles.length === 0 ? (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700" data-testid="no-approved-vehicles-msg">
                <AlertCircle size={16} />
                <span>Keine freigegebenen Fahrzeuge vorhanden. Bitte warten Sie auf die Admin-Freigabe.</span>
              </div>
            ) : (
              <div className="space-y-2">
                {approvedVehicles.map(v => (
                  <label
                    key={v.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all w-full ${
                      selectedVehicleId === v.id
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                    data-testid={`vehicle-option-${v.id}`}
                  >
                    <input
                      type="radio"
                      name="vehicle"
                      value={v.id}
                      checked={selectedVehicleId === v.id}
                      onChange={() => setSelectedVehicleId(v.id)}
                      className="w-4 h-4 text-orange-500 focus:ring-orange-500 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-slate-800 truncate block">{v.make} {v.model}</span>
                      <span className="text-xs text-slate-400 block truncate">{v.plate_number}</span>
                    </div>
                    <div className="hidden sm:flex items-center gap-1.5 text-xs text-emerald-600 shrink-0">
                      <CheckCircle size={12} />
                      <span>Freigegeben</span>
                    </div>
                    <CheckCircle size={16} className="sm:hidden text-emerald-500 shrink-0" />
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Route */}
          <div className="bg-slate-50 rounded-xl p-3 sm:p-4">
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center pt-1 shrink-0">
                <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                <div className="w-0.5 h-6 bg-slate-300" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-sm text-slate-800 truncate flex-1">{formatLocation(request.pickup_location)}</p>
                  <button onClick={() => openInGoogleMaps(request.pickup_location?.lat, request.pickup_location?.lng)} className="text-blue-500 hover:text-blue-700 shrink-0">
                    <ExternalLink size={14} />
                  </button>
                </div>
                <div className="my-4" />
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-sm text-slate-800 truncate flex-1">{formatLocation(request.dropoff_location)}</p>
                  <button onClick={() => openInGoogleMaps(request.dropoff_location?.lat, request.dropoff_location?.lng)} className="text-blue-500 hover:text-blue-700 shrink-0">
                    <ExternalLink size={14} />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-200">
              <span className="text-xs text-slate-500">{request.distance_km} km</span>
              <span className="text-xs text-slate-500">~{request.duration_minutes} Min.</span>
              <span className="text-xs text-slate-500">{request.adults + (request.children || 0)} Pers.</span>
              {request.flight_number && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Plane size={12} /> {request.flight_number}
                </span>
              )}
            </div>
          </div>

          {/* AI Price Suggestion */}
          {suggestedPrice && (
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={16} className="text-blue-600" />
                <span className="text-sm font-medium text-blue-800">{t('driver_price_suggestion')}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-blue-700">&euro;{suggestedPrice.suggested_price}</span>
                <span className="text-sm text-blue-500">{t('driver_price_recommended')}</span>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                {t('driver_price_range')}: &euro;{suggestedPrice.min_price} - &euro;{suggestedPrice.max_price}
              </p>
            </div>
          )}

          {/* Price Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('driver_your_price')} *</label>
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200 focus-within:border-orange-500">
              <DollarSign size={18} className="text-slate-400" />
              <input
                type="number"
                value={offerPrice}
                onChange={(e) => setOfferPrice(e.target.value)}
                placeholder={t('driver_price_placeholder')}
                className="flex-1 bg-transparent outline-none text-lg font-semibold"
                data-testid="offer-price-input"
              />
              <span className="text-slate-400">EUR</span>
            </div>
            {/* Net price breakdown (live calculation) */}
            {priceNum > 0 && (
              <div className="mt-3 bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm" data-testid="net-price-breakdown">
                <div className="flex items-center justify-between text-slate-600">
                  <span>Ihr Preis</span>
                  <span className="font-medium">€{priceNum.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-rose-600 mt-1">
                  <span>Portal-Kommission ({commissionPercent}%)</span>
                  <span className="font-medium">−€{commissionEur.toFixed(2)}</span>
                </div>
                <div className="border-t border-slate-200 mt-2 pt-2 flex items-center justify-between">
                  <span className="font-semibold text-slate-800">Netto-Auszahlung</span>
                  <span className="font-bold text-emerald-600 text-base" data-testid="net-price-value">€{netEur.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('driver_notes_label')}</label>
            <textarea
              value={offerNotes}
              onChange={(e) => setOfferNotes(e.target.value)}
              placeholder={t('driver_notes_placeholder')}
              rows={2}
              className="w-full px-4 py-3 bg-slate-50 rounded-xl border border-slate-200 focus:border-orange-500 outline-none text-sm resize-none"
              data-testid="offer-notes-input"
            />
          </div>

          {/* Mini Map */}
          {request?.pickup_location && request?.dropoff_location && (
            <div className="relative">
              <div className="rounded-xl overflow-hidden border border-slate-200">
                <MapContainer
                  center={[
                    (request.pickup_location.lat + request.dropoff_location.lat) / 2,
                    (request.pickup_location.lng + request.dropoff_location.lng) / 2
                  ]}
                  zoom={10}
                  className="w-full h-[130px] sm:h-[180px]"
                  style={{ zIndex: 1 }}
                  scrollWheelZoom={false}
                  dragging={false}
                >
                  <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[request.pickup_location.lat, request.pickup_location.lng]} icon={startIcon} />
                  <Marker position={[request.dropoff_location.lat, request.dropoff_location.lng]} icon={endIcon} />
                  {routeCoordinates.length > 0 && (
                    <Polyline positions={routeCoordinates} pathOptions={{ color: '#22C55E', weight: 4, opacity: 0.9 }} />
                  )}
                  <FitBounds from={request.pickup_location} to={request.dropoff_location} />
                </MapContainer>
              </div>
              <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border border-slate-100 z-[1000]">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Navigation size={14} className="text-emerald-600" />
                    <span className="text-sm font-bold text-slate-800">{request.distance_km || '?'} km</span>
                  </div>
                  <div className="w-px h-4 bg-slate-200" />
                  <div className="flex items-center gap-1.5">
                    <Clock size={14} className="text-emerald-600" />
                    <span className="text-sm font-bold text-slate-800">~{request.duration_minutes || '?'} min</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 sm:px-6 py-2 shrink-0 border-t border-slate-100">
          <label className="flex items-start gap-2 cursor-pointer group" data-testid="driver-terms-checkbox-label">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500 cursor-pointer shrink-0"
              data-testid="driver-terms-checkbox"
            />
            <span className="text-xs text-slate-600 leading-snug">
              {t('driver_terms_accept_text')}{' '}
              <a href="/fahrer-agb" target="_blank" rel="noreferrer" className="text-orange-500 hover:underline font-medium">
                {t('driver_terms_link')}
              </a>.{' '}
              {t('driver_terms_binding_text')}
            </span>
          </label>
        </div>
        <div className="p-4 sm:p-6 border-t border-slate-100 flex gap-3 shrink-0 bg-white">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
          >
            {t('driver_cancel')}
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting || !offerPrice || !acceptedTerms || !selectedVehicleId || approvedVehicles.length === 0}
            className="flex-1 py-3 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="submit-offer-btn"
          >
            {submitting ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <>
                <Send size={18} /> {t('driver_submit_offer')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
